import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { getOrder, type Order } from "@/lib/checkout/order";
import { formatPrice } from "@/lib/cart/pricing";

export const Route = createFileRoute("/invoice/$id")({
  head: ({ params }) => ({
    meta: [
      { title: `Invoice ${params.id} — CB16 Maison` },
      { name: "description", content: "Printable invoice for your CB16 Maison order." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InvoicePage,
});

function InvoicePage() {
  const { id } = Route.useParams();
  const [order, setOrder] = useState<Order | null | undefined>(undefined);

  useEffect(() => {
    setOrder(getOrder(id));
  }, [id]);

  if (order === undefined)
    return <div className="p-16 text-center text-sm text-black/50">Loading invoice…</div>;
  if (order === null)
    return (
      <div className="p-16 text-center text-sm text-black/50">
        Invoice not found on this device.
      </div>
    );

  return (
    <div className="bg-white text-black">
      <div className="mx-auto max-w-3xl px-8 py-12 print:px-0 print:py-0">
        <div className="flex items-start justify-between gap-6 border-b border-black/20 pb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#C6A15B]">CB16 Maison</p>
            <h1 className="mt-2 text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              Invoice
            </h1>
            <p className="mt-1 text-sm text-black/60">INV-{order.id}</p>
          </div>
          <div className="text-right text-sm text-black/70">
            <p>{new Date(order.createdAt).toLocaleDateString()}</p>
            <p className="mt-1">Order {order.id}</p>
          </div>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-black/60">Billed To</p>
            <address className="not-italic mt-2 text-black/80">
              {order.billing.fullName}
              <br />
              {order.billing.line1}
              {order.billing.line2 ? (
                <>
                  <br />
                  {order.billing.line2}
                </>
              ) : null}
              <br />
              {order.billing.city}, {order.billing.postalCode}
              <br />
              {order.billing.country}
              <br />
              {order.email}
            </address>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-black/60">Shipped To</p>
            <address className="not-italic mt-2 text-black/80">
              {order.shipping.fullName}
              <br />
              {order.shipping.line1}
              {order.shipping.line2 ? (
                <>
                  <br />
                  {order.shipping.line2}
                </>
              ) : null}
              <br />
              {order.shipping.city}, {order.shipping.postalCode}
              <br />
              {order.shipping.country}
            </address>
          </div>
        </div>

        <table className="mt-10 w-full text-sm">
          <thead>
            <tr className="border-b border-black/20 text-[11px] uppercase tracking-[0.22em] text-black/60">
              <th className="py-2 text-left">Item</th>
              <th className="py-2 text-right">Qty</th>
              <th className="py-2 text-right">Unit</th>
              <th className="py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={`${l.productId}:${l.variantId}`} className="border-b border-black/10">
                <td className="py-3">
                  <div>{l.name}</div>
                  <div className="text-[11px] text-black/50">{l.variantLabel}</div>
                </td>
                <td className="py-3 text-right tabular-nums">{l.quantity}</td>
                <td className="py-3 text-right tabular-nums">{formatPrice(l.unitPrice)}</td>
                <td className="py-3 text-right tabular-nums">
                  {formatPrice(l.unitPrice * l.quantity)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {order.pricing.gst && order.pricing.gst.mode !== "none" ? (
          <div className="mt-6 grid gap-1 text-[11px] uppercase tracking-[0.22em] text-black/60">
            {order.pricing.gst.gstin ? <p>Seller GSTIN: {order.pricing.gst.gstin}</p> : null}
            {order.pricing.gst.customerGstin ? (
              <p>Customer GSTIN: {order.pricing.gst.customerGstin}</p>
            ) : null}
            {order.pricing.gst.placeOfSupply ? (
              <p>Place of Supply: {order.pricing.gst.placeOfSupply}</p>
            ) : null}
            {order.pricing.gst.hsn ? <p>HSN: {order.pricing.gst.hsn}</p> : null}
          </div>
        ) : null}

        <div className="mt-6 ml-auto w-full max-w-xs space-y-1 text-sm">
          <Row label="Subtotal" value={formatPrice(order.pricing.subtotal)} />
          {order.pricing.discount > 0 && (
            <Row label="Discount" value={`− ${formatPrice(order.pricing.discount)}`} />
          )}
          <Row
            label={`Shipping · ${order.delivery.label}`}
            value={order.pricing.shipping === 0 ? "Free" : formatPrice(order.pricing.shipping)}
          />
          {order.pricing.gst && order.pricing.gst.mode !== "none" ? (
            <>
              <Row label="Taxable Value" value={formatPrice(order.pricing.gst.taxable)} />
              {order.pricing.gst.mode === "cgst_sgst" ? (
                <>
                  <Row
                    label={`CGST @ ${order.pricing.gst.cgstRatePct ?? order.pricing.gst.ratePct / 2}%`}
                    value={formatPrice(order.pricing.gst.cgst)}
                  />
                  <Row
                    label={`SGST @ ${order.pricing.gst.sgstRatePct ?? order.pricing.gst.ratePct / 2}%`}
                    value={formatPrice(order.pricing.gst.sgst)}
                  />
                </>
              ) : (
                <Row
                  label={`IGST @ ${order.pricing.gst.igstRatePct ?? order.pricing.gst.ratePct}%`}
                  value={formatPrice(order.pricing.gst.igst)}
                />
              )}
              <Row
                label={`Total ${order.pricing.gst.label}${order.pricing.gst.inclusive ? " (incl.)" : ""}`}
                value={formatPrice(order.pricing.gst.totalGst)}
              />
            </>
          ) : null}
          <div className="mt-2 border-t border-black/20 pt-2">
            <Row label="Grand Total" value={formatPrice(order.pricing.total)} strong />
          </div>
        </div>


        <div className="mt-12 border-t border-black/10 pt-4 text-[11px] uppercase tracking-[0.22em] text-black/50">
          Thank you for choosing CB16 Maison.
        </div>

        <div className="mt-8 flex justify-end gap-3 print:hidden">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 border border-black bg-black px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
          >
            <Printer className="h-3.5 w-3.5" /> Print / Save PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-[11px] uppercase tracking-[0.22em] text-black/60">{label}</span>
      <span className={"tabular-nums " + (strong ? "text-base font-medium" : "")}>{value}</span>
    </div>
  );
}